import React, { useEffect, useRef } from 'react'
import * as THREE from 'three'
// Use @types/three: import types from extensionless module specifiers
import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader'
import type { TransformControls } from 'three/examples/jsm/controls/TransformControls'
export type Snapshot3DProps = {
  /** GLB/GLTF file URL (GLB recommended) */
  modelUrl: string
  /** Rotation in degrees (XYZ) */
  rotationDeg?: { x: number; y: number; z: number }
  /** Model translation (optional) */
  position?: { x: number; y: number; z: number }
  /** Background color hex (e.g. 0x0d0d0d) */
  background?: number
  /** Styling/classes for page placement */
  className?: string
  style?: React.CSSProperties
  /** If true, enable interactive orbit controls (mouse/touch). */
  interactive?: boolean
  /** Allow manual rotation tweaking via controls/gizmo. */
  allowManualRotation?: boolean
  /** Callback fired whenever manual rotation changes (deg). */
  onRotationChange?: (rotation: { x: number; y: number; z: number }) => void
  /** Callback fired when user stores the current rotation. */
  onRotationSave?: (sample: {
    id: string
    rotation: { x: number; y: number; z: number }
    sensor: { x: number; y: number; z: number }
    createdAt: number
  }) => void
}

// Theme-aware default backgrounds
const BG_DARK = 0x1b2635 // existing dark blue
const BG_LIGHT = 0xf7f9fc // requested light background

function getCurrentTheme(): 'light' | 'dark' {
  try {
    const root = document.documentElement
    const body = document.body

    // Prefer explicit app theme hints on html/body
    const getAttr = (el: HTMLElement | null, name: string) =>
      (el?.getAttribute?.(name) || '').toLowerCase()
    const dsHtml = (root.getAttribute('data-theme') || root.dataset?.theme || '').toLowerCase()
    const dsBody = (body?.getAttribute('data-theme') || body?.dataset?.theme || '').toLowerCase()
    const muiHtml = getAttr(root, 'data-mui-color-scheme')
    const muiBody = getAttr(body, 'data-mui-color-scheme')
    const genericHtml = getAttr(root, 'data-color-scheme')
    const genericBody = getAttr(body, 'data-color-scheme')

    const candidates = [dsHtml, dsBody, muiHtml, muiBody, genericHtml, genericBody]
    for (const val of candidates) {
      if (val === 'dark' || val === 'light') return val as 'dark' | 'light'
    }

    // Class-based hints
    if (root.classList.contains('dark') || body?.classList.contains('dark')) return 'dark'
    if (root.classList.contains('light') || body?.classList.contains('light')) return 'light'

    // Fallback: OS preference
    const mm = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)')
    return mm && mm.matches ? 'dark' : 'light'
  } catch {
    return 'light'
  }
}

const Snapshot3D: React.FC<Snapshot3DProps> = ({
  modelUrl,
  rotationDeg = { x: 0, y: 0, z: 0 },
  position,
  // If not provided, background auto-follows theme (dark: #1b2635, light: #f7f9fc)
  background,
  className,
  style,
  interactive = false,
  allowManualRotation = false,
  onRotationChange,
  onRotationSave
}) => {
  const hostRef = useRef<HTMLDivElement | null>(null)
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null)
  const sceneRef = useRef<THREE.Scene | null>(null)
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null)
  const modelRef = useRef<THREE.Object3D | null>(null)
  type OrbitControlsLike = {
    dispose: () => void
    update: () => void
    minPolarAngle: number
    maxPolarAngle: number
    getPolarAngle?: () => number
    getAzimuthalAngle?: () => number
    enabled: boolean
    enableRotate: boolean
    minAzimuthAngle: number
    maxAzimuthAngle: number
  }
  const controlsRef = useRef<OrbitControlsLike | null>(null)
  const animRef = useRef<number | null>(null)
  const transformDisposeRef = useRef<(() => void) | null>(null)

  // Resolve background: prop overrides theme; otherwise pick based on current theme
  const [resolvedBg, setResolvedBg] = React.useState<number>(() =>
    typeof background === 'number' ? background : getCurrentTheme() === 'dark' ? BG_DARK : BG_LIGHT
  )
  const wrapDeg = (deg: number) => ((((deg + 180) % 360) + 360) % 360) - 180
  const [manualRotation, setManualRotation] = React.useState<{ x: number; y: number; z: number }>(
    () => ({
      x: wrapDeg(rotationDeg.x ?? 0),
      y: wrapDeg(rotationDeg.y ?? 0),
      z: wrapDeg(rotationDeg.z ?? 0)
    })
  )
  const [savedRotations, setSavedRotations] = React.useState<
    Array<{
      id: string
      rotation: { x: number; y: number; z: number }
      sensor: { x: number; y: number; z: number }
      createdAt: number
    }>
  >([])
  const [sensorValues, setSensorValues] = React.useState<{ x: number; y: number; z: number }>(
    () => ({ x: 0, y: 0, z: 0 })
  )
  const [cameraLocked, setCameraLocked] = React.useState<boolean>(true)

  const renderSnapshot = React.useCallback(() => {
    if (interactive) return
    const scene = sceneRef.current
    const camera = cameraRef.current
    const renderer = rendererRef.current
    if (!scene || !camera || !renderer) return
    try {
      renderer.render(scene, camera)
    } catch {
      /* ignore */
    }
  }, [interactive])

  const applyRotationToModel = React.useCallback(
    (rot: { x: number; y: number; z: number }) => {
      const model = modelRef.current
      if (!model) return
      model.rotation.set(
        THREE.MathUtils.degToRad(rot.x),
        THREE.MathUtils.degToRad(rot.y),
        THREE.MathUtils.degToRad(rot.z),
        'XYZ'
      )
      renderSnapshot()
    },
    [renderSnapshot]
  )

  useEffect(() => {
    if (!allowManualRotation) {
      setSavedRotations([])
      setSensorValues({ x: 0, y: 0, z: 0 })
      setCameraLocked(true)
    }
  }, [allowManualRotation])

  // Keep resolvedBg in sync with prop and theme changes
  useEffect(() => {
    if (typeof background === 'number') {
      setResolvedBg(background)
      return
    }

    const applyTheme = () => {
      const theme = getCurrentTheme()
      setResolvedBg(theme === 'dark' ? BG_DARK : BG_LIGHT)
    }

    applyTheme()

    // Observe data-theme/class changes on <html>
    const root = document.documentElement
    const body = document.body
    const mo = new MutationObserver(applyTheme)
    try {
      const attrs = ['class', 'data-theme', 'data-mui-color-scheme', 'data-color-scheme']
      mo.observe(root, { attributes: true, attributeFilter: attrs })
      if (body) mo.observe(body, { attributes: true, attributeFilter: attrs })
    } catch {
      /* noop */
    }

    // Listen to OS color scheme changes
    let mm: MediaQueryList | null = null
    let mqlHandler: ((e: MediaQueryListEvent) => void) | null = null
    try {
      mm = window.matchMedia('(prefers-color-scheme: dark)')
      mqlHandler = () => applyTheme()
      if (typeof mm.addEventListener === 'function') mm.addEventListener('change', mqlHandler)
      else if (
        typeof (mm as unknown as { addListener?: (cb: (e: MediaQueryListEvent) => void) => void })
          .addListener === 'function'
      )
        (
          mm as unknown as { addListener: (cb: (e: MediaQueryListEvent) => void) => void }
        ).addListener(mqlHandler)
    } catch {
      /* noop */
    }

    return () => {
      mo.disconnect()
      if (mm && mqlHandler) {
        try {
          if (typeof mm.removeEventListener === 'function')
            mm.removeEventListener('change', mqlHandler)
          else if (
            typeof (
              mm as unknown as { removeListener?: (cb: (e: MediaQueryListEvent) => void) => void }
            ).removeListener === 'function'
          )
            (
              mm as unknown as { removeListener: (cb: (e: MediaQueryListEvent) => void) => void }
            ).removeListener(mqlHandler)
        } catch {
          /* noop */
        }
      }
    }
  }, [background])

  useEffect(() => {
    const next = {
      x: wrapDeg(rotationDeg.x ?? 0),
      y: wrapDeg(rotationDeg.y ?? 0),
      z: wrapDeg(rotationDeg.z ?? 0)
    }
    setManualRotation(next)
    if (allowManualRotation) {
      applyRotationToModel(next)
    }
  }, [allowManualRotation, applyRotationToModel, rotationDeg.x, rotationDeg.y, rotationDeg.z])

  const handleAxisInput = React.useCallback(
    (axis: 'x' | 'y' | 'z', value: number) => {
      if (!Number.isFinite(value)) return
      const wrapped = wrapDeg(value)
      setManualRotation((prev) => {
        if (Math.abs(prev[axis] - wrapped) < 0.001) return prev
        const next = { ...prev, [axis]: wrapped }
        applyRotationToModel(next)
        onRotationChange?.(next)
        return next
      })
    },
    [applyRotationToModel, onRotationChange]
  )

  const handleAddRotation = React.useCallback(() => {
    const sample = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      rotation: { ...manualRotation },
      sensor: { ...sensorValues },
      createdAt: Date.now()
    }
    setSavedRotations((prev) => [...prev, sample])
    onRotationSave?.(sample)
  }, [manualRotation, onRotationSave, sensorValues])

  const handleSensorInput = React.useCallback((axis: 'x' | 'y' | 'z', value: number) => {
    if (!Number.isFinite(value)) return
    const clamped = Math.max(-2000, Math.min(2000, value))
    setSensorValues((prev) => {
      if (Math.abs(prev[axis] - clamped) < 0.001) return prev
      return { ...prev, [axis]: clamped }
    })
  }, [])

  const applyCameraLockState = React.useCallback(
    (locked: boolean) => {
      if (!allowManualRotation) return
      const controls = controlsRef.current
      if (!controls) return
      if (locked) {
        const azimuth =
          typeof controls.getAzimuthalAngle === 'function' ? controls.getAzimuthalAngle() : 0
        const polar =
          typeof controls.getPolarAngle === 'function' ? controls.getPolarAngle() : Math.PI / 2
        controls.enableRotate = false
        controls.minPolarAngle = polar
        controls.maxPolarAngle = polar
        controls.minAzimuthAngle = azimuth
        controls.maxAzimuthAngle = azimuth
      } else {
        controls.enableRotate = true
        controls.minPolarAngle = 0
        controls.maxPolarAngle = Math.PI
        controls.minAzimuthAngle = -Infinity
        controls.maxAzimuthAngle = Infinity
      }
      controls.update()
    },
    [allowManualRotation]
  )

  useEffect(() => {
    applyCameraLockState(cameraLocked)
  }, [applyCameraLockState, cameraLocked])

  // Luo perus Three-objektit kerran
  useEffect(() => {
    const host = hostRef.current!
    const scene = new THREE.Scene()
    scene.background = new THREE.Color(resolvedBg)

    const camera = new THREE.PerspectiveCamera(45, 1, 0.01, 1000)

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      preserveDrawingBuffer: true // keep one rendered frame on the canvas
    })

    rendererRef.current = renderer
    sceneRef.current = scene
    cameraRef.current = camera

    // Ensure host clips overflow so canvas cannot overlap neighboring UI
    host.style.position = host.style.position || 'relative'
    host.style.overflow = 'hidden'

    // Make sure the canvas fills the host and is positioned inside it
    renderer.domElement.style.position = 'absolute'
    renderer.domElement.style.top = '0'
    renderer.domElement.style.left = '0'
    renderer.domElement.style.width = '100%'
    renderer.domElement.style.height = '100%'
    renderer.domElement.style.display = 'block'
    renderer.domElement.style.touchAction = 'none'
    renderer.domElement.style.pointerEvents = 'auto'

    host.appendChild(renderer.domElement)

    // Light-weight lighting setup
    scene.add(new THREE.AmbientLight(0xffffff, 0.7))
    const dir = new THREE.DirectionalLight(0xffffff, 1.0)
    dir.position.set(3, 5, 2)
    scene.add(dir)

    // Cleanup on unmount
    return () => {
      if (rendererRef.current) {
        rendererRef.current.dispose()
        if (rendererRef.current.domElement.parentNode) {
          rendererRef.current.domElement.parentNode.removeChild(rendererRef.current.domElement)
        }
      }
      sceneRef.current = null
      cameraRef.current = null
      modelRef.current = null
    }
  }, [])

  // Apply background updates to scene without reloading model
  useEffect(() => {
    const scene = sceneRef.current
    const renderer = rendererRef.current
    const camera = cameraRef.current
    if (!scene || !renderer || !camera) return
    scene.background = new THREE.Color(resolvedBg)
    // Re-render a frame in snapshot mode; interactive mode renders continuously
    if (!interactive) {
      try {
        renderer.render(scene, camera)
      } catch {
        /* ignore */
      }
    }
  }, [resolvedBg, interactive])

  // Load model and render a frame whenever modelUrl/rotation/position changes
  useEffect(() => {
    let cancelled = false
    const host = hostRef.current
    const scene = sceneRef.current
    const camera = cameraRef.current
    const renderer = rendererRef.current

    if (!host || !scene || !camera || !renderer) return

    // Set renderer size to container dimensions
    const fitRenderer = () => {
      // prefer the actual canvas display size if set by CSS
      const canvas = renderer.domElement
      const w = canvas.clientWidth || host.clientWidth || 1
      const h = canvas.clientHeight || host.clientHeight || 1
      camera.aspect = w / h
      camera.updateProjectionMatrix()
      renderer.setSize(w, h, false)
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    }
    fitRenderer()

    const handleResize = () => fitRenderer()
    window.addEventListener('resize', handleResize)

    // Remove old model
    if (modelRef.current) {
      scene.remove(modelRef.current)
      modelRef.current = null
    }

    // Dynamic import for loader (smaller initial bundle)
    ;(async () => {
      const lower = modelUrl.toLowerCase()
      let root: THREE.Object3D | null = null

      if (lower.endsWith('.glb') || lower.endsWith('.gltf')) {
        const { GLTFLoader } = (await import(
          'three/examples/jsm/loaders/GLTFLoader'
        )) as typeof import('three/examples/jsm/loaders/GLTFLoader')
        root = await new Promise<THREE.Object3D>((resolve, reject) => {
          new GLTFLoader().load(modelUrl, (gltf: GLTF) => resolve(gltf.scene), undefined, reject)
        })
      } else {
        throw new Error(
          'Suositus on GLB/GLTF. (OBJ onnistuu lisätyllä OBJLoaderilla, mutta GLB on kevyempi.)'
        )
      }

      if (cancelled) return

      // Apply rotation & optional position
      const initialRotation = allowManualRotation ? manualRotation : rotationDeg
      root.rotation.set(
        THREE.MathUtils.degToRad(initialRotation.x),
        THREE.MathUtils.degToRad(initialRotation.y),
        THREE.MathUtils.degToRad(initialRotation.z),
        'XYZ'
      )
      if (position) root.position.set(position.x, position.y, position.z)

      // Ensure materials exist if missing
      root.traverse((obj: THREE.Object3D) => {
        const mesh = obj as THREE.Mesh
        if ((mesh as unknown as { isMesh?: boolean }).isMesh) {
          if (!mesh.material) {
            mesh.material = new THREE.MeshStandardMaterial({ color: 0xcccccc })
          }
          mesh.castShadow = false
          mesh.receiveShadow = false
        }
      })

      scene.add(root)
      modelRef.current = root

      if (allowManualRotation) {
        setManualRotation({
          x: wrapDeg(initialRotation.x),
          y: wrapDeg(initialRotation.y),
          z: wrapDeg(initialRotation.z)
        })
      }

      // Fit camera to model in its current pose
      const box = new THREE.Box3().setFromObject(root)
      const size = box.getSize(new THREE.Vector3())
      const center = box.getCenter(new THREE.Vector3())

      const radius = size.length() * 0.6 + 0.001
      const halfFov = THREE.MathUtils.degToRad(camera.fov / 2)
      const dist = radius / Math.tan(halfFov)

      camera.near = Math.max(0.01, dist / 1000)
      camera.far = dist * 10
      const cameraOffset = allowManualRotation
        ? new THREE.Vector3(0, dist * 0.2, dist)
        : new THREE.Vector3(dist, dist * 0.25, dist)
      camera.position.copy(center).add(cameraOffset)
      camera.lookAt(center)
      camera.updateProjectionMatrix()

      if (interactive) {
        // Enable orbit controls and continuous rendering for interactivity
        ;(async () => {
          try {
            const { OrbitControls } = (await import(
              'three/examples/jsm/controls/OrbitControls'
            )) as typeof import('three/examples/jsm/controls/OrbitControls')
            // debug: confirm controls loaded

            console.debug('OrbitControls loaded')
            const controls = new OrbitControls(camera, renderer.domElement)
            controls.enablePan = false
            controls.enableDamping = true
            controls.dampingFactor = 0.07
            controls.target.copy(center)
            controls.update()
            if (!allowManualRotation) {
              // Lock vertical movement (polar angle) to current pitch so user can only rotate around Y axis
              try {
                const polar =
                  typeof controls.getPolarAngle === 'function'
                    ? controls.getPolarAngle()
                    : Math.PI / 2
                controls.minPolarAngle = polar
                controls.maxPolarAngle = polar
              } catch (_err) {
                // If any error, fall back to locking at 90 degrees
                controls.minPolarAngle = Math.PI / 2
                controls.maxPolarAngle = Math.PI / 2
              }
            }
            controlsRef.current = controls
            if (allowManualRotation) {
              applyCameraLockState(cameraLocked)
            }

            const animate = () => {
              controls.update()
              renderer.render(scene, camera)
              animRef.current = requestAnimationFrame(animate)
            }

            animate()
          } catch (err) {
            console.error('Failed to load OrbitControls', err)
            renderer.render(scene, camera)
          }
        })()
      } else {
        // Render a single frame for snapshot mode
        renderer.render(scene, camera)
      }

      if (allowManualRotation) {
        ;(async () => {
          try {
            const model = modelRef.current
            if (!model) return
            const { TransformControls: TransformControlsCtor } = (await import(
              'three/examples/jsm/controls/TransformControls'
            )) as typeof import('three/examples/jsm/controls/TransformControls')
            const controls: TransformControls = new TransformControlsCtor(
              camera,
              renderer.domElement
            )
            controls.setMode('rotate')
            controls.setSpace('local')
            controls.attach(model)
            scene.add(controls as unknown as THREE.Object3D)
            const handleChange = () => {
              const current = modelRef.current
              if (!current) return
              const updated = {
                x: wrapDeg(THREE.MathUtils.radToDeg(current.rotation.x)),
                y: wrapDeg(THREE.MathUtils.radToDeg(current.rotation.y)),
                z: wrapDeg(THREE.MathUtils.radToDeg(current.rotation.z))
              }
              setManualRotation((prev) => {
                const delta =
                  Math.abs(prev.x - updated.x) +
                  Math.abs(prev.y - updated.y) +
                  Math.abs(prev.z - updated.z)
                if (delta < 0.01) return prev
                onRotationChange?.(updated)
                return updated
              })
              if (!interactive) {
                try {
                  renderer.render(scene, camera)
                } catch {
                  /* ignore */
                }
              }
            }
            const handleDrag = (event: THREE.Event & { value?: unknown }) => {
              if (controlsRef.current) {
                controlsRef.current.enabled = !event.value
              }
            }
            controls.addEventListener('change', handleChange)
            controls.addEventListener('dragging-changed', handleDrag)
            transformDisposeRef.current = () => {
              controls.removeEventListener('change', handleChange)
              controls.removeEventListener('dragging-changed', handleDrag)
              try {
                controls.detach()
              } catch {
                /* ignore */
              }
              scene.remove(controls as unknown as THREE.Object3D)
              controls.dispose()
            }
          } catch (err) {
            console.error('Failed to load TransformControls', err)
          }
        })()
      }
    })().catch(console.error)

    return () => {
      cancelled = true
      if (animRef.current) {
        cancelAnimationFrame(animRef.current)
        animRef.current = null
      }
      if (controlsRef.current) {
        try {
          controlsRef.current.dispose()
        } catch (_e) {
          // ignore
        }
        controlsRef.current = null
      }
      if (transformDisposeRef.current) {
        try {
          transformDisposeRef.current()
        } catch {
          /* ignore */
        }
        transformDisposeRef.current = null
      }
      window.removeEventListener('resize', handleResize)
    }
    // Note: background changes are handled separately to avoid reloading the model
  }, [
    modelUrl,
    rotationDeg.x,
    rotationDeg.y,
    rotationDeg.z,
    position?.x,
    position?.y,
    position?.z,
    allowManualRotation,
    interactive
  ])

  return (
    <div
      ref={hostRef}
      className={className}
      style={{
        // Default to fill parent's size so parent components can control sizing responsively
        width: '100%',
        height: '100%',
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        ...style
      }}
    >
      {allowManualRotation && (
        <div
          style={{
            position: 'absolute',
            top: 16,
            right: 16,
            maxWidth: 'min(420px, 45vw)',
            background: 'rgba(15,23,42,0.82)',
            color: '#f8fafc',
            padding: '12px 16px',
            borderRadius: 10,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 16,
            alignItems: 'flex-start',
            boxShadow: '0 18px 36px rgba(15,23,42,0.45)',
            backdropFilter: 'blur(6px)',
            zIndex: 5,
            minWidth: 280
          }}
        >
          <div
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12
            }}
          >
            <span style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.6 }}>
              Manuaalinen säätö
            </span>
            <button
              type="button"
              onClick={() => setCameraLocked((prev) => !prev)}
              style={{
                padding: '6px 14px',
                borderRadius: 999,
                border: '1px solid rgba(148,163,184,0.3)',
                background: cameraLocked ? 'rgba(37,99,235,0.85)' : 'rgba(15,23,42,0.6)',
                color: '#f8fafc',
                fontSize: 11,
                letterSpacing: 0.6,
                textTransform: 'uppercase',
                cursor: 'pointer',
                transition: 'background 0.2s ease'
              }}
            >
              {cameraLocked ? 'Vapauta kamera' : 'Lukitse kamera'}
            </button>
          </div>
          {(['x', 'y', 'z'] as const).map((axis) => (
            <div key={axis} style={{ minWidth: 160 }}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 6,
                  fontSize: 12,
                  textTransform: 'uppercase',
                  letterSpacing: 0.6
                }}
              >
                {axis.toUpperCase()}-akseli
                <input
                  type="number"
                  value={manualRotation[axis]}
                  min={-360}
                  max={360}
                  step={0.1}
                  onChange={(event) => handleAxisInput(axis, Number(event.target.value))}
                  style={{
                    width: 72,
                    borderRadius: 4,
                    border: '1px solid rgba(148,163,184,0.35)',
                    background: 'rgba(15,23,42,0.9)',
                    color: 'inherit',
                    padding: '4px 6px'
                  }}
                />
              </label>
              <input
                type="range"
                min={-180}
                max={180}
                step={0.5}
                value={manualRotation[axis]}
                onChange={(event) => handleAxisInput(axis, Number(event.target.value))}
                style={{ width: '100%' }}
              />
            </div>
          ))}
          <div
            style={{
              width: '100%',
              marginTop: 12,
              paddingTop: 12,
              borderTop: '1px solid rgba(148,163,184,0.25)'
            }}
          >
            <div
              style={{
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: 0.6,
                marginBottom: 8,
                opacity: 0.8
              }}
            >
              Laitearvot (raw, -1000…1000)
            </div>
            {(['x', 'y', 'z'] as const).map((axis) => (
              <div key={`sensor-${axis}`} style={{ marginBottom: 10 }}>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 6,
                    fontSize: 12,
                    textTransform: 'uppercase',
                    letterSpacing: 0.6
                  }}
                >
                  {axis.toUpperCase()}-raaka
                  <input
                    type="number"
                    value={sensorValues[axis]}
                    min={-2000}
                    max={2000}
                    step={1}
                    onChange={(event) => handleSensorInput(axis, Number(event.target.value))}
                    style={{
                      width: 86,
                      borderRadius: 4,
                      border: '1px solid rgba(148,163,184,0.35)',
                      background: 'rgba(15,23,42,0.9)',
                      color: 'inherit',
                      padding: '4px 6px'
                    }}
                  />
                </label>
                <input
                  type="range"
                  min={-1200}
                  max={1200}
                  step={1}
                  value={sensorValues[axis]}
                  onChange={(event) => handleSensorInput(axis, Number(event.target.value))}
                  style={{ width: '100%' }}
                />
              </div>
            ))}
          </div>
          <div
            style={{
              display: 'flex',
              gap: 12,
              alignItems: 'center',
              justifyContent: 'flex-start',
              width: '100%',
              marginTop: 4
            }}
          >
            <button
              type="button"
              onClick={handleAddRotation}
              style={{
                padding: '6px 16px',
                borderRadius: 999,
                border: '1px solid rgba(148,163,184,0.45)',
                background: 'rgba(37,99,235,0.8)',
                color: '#f8fafc',
                fontSize: 12,
                letterSpacing: 0.4,
                textTransform: 'uppercase',
                cursor: 'pointer'
              }}
            >
              Lisää piste
            </button>
            {savedRotations.length > 0 && (
              <span style={{ fontSize: 12, opacity: 0.85 }}>
                {savedRotations.length} tallennettu
              </span>
            )}
          </div>
          {savedRotations.length > 0 && (
            <div style={{ width: '100%', marginTop: 8 }}>
              <div
                style={{
                  fontSize: 11,
                  textTransform: 'uppercase',
                  letterSpacing: 0.6,
                  marginBottom: 4,
                  opacity: 0.8
                }}
              >
                Tallennetut asennot
              </div>
              <ul
                style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                  maxHeight: 140,
                  overflowY: 'auto'
                }}
              >
                {savedRotations.map((sample, index) => (
                  <li
                    key={sample.id}
                    style={{
                      display: 'flex',
                      gap: 12,
                      fontSize: 12,
                      padding: '4px 6px',
                      borderRadius: 6,
                      background: 'rgba(15,23,42,0.6)',
                      border: '1px solid rgba(148,163,184,0.2)',
                      flexWrap: 'wrap'
                    }}
                  >
                    <span style={{ opacity: 0.7 }}>#{index + 1}</span>
                    <span>X: {sample.rotation.x.toFixed(2)}°</span>
                    <span>Y: {sample.rotation.y.toFixed(2)}°</span>
                    <span>Z: {sample.rotation.z.toFixed(2)}°</span>
                    <span style={{ opacity: 0.7 }}>|</span>
                    <span>rawX: {sample.sensor.x.toFixed(0)}</span>
                    <span>rawY: {sample.sensor.y.toFixed(0)}</span>
                    <span>rawZ: {sample.sensor.z.toFixed(0)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default Snapshot3D
